/* eslint-disable max-lines-per-function */
import './CoralVideoHero.scss'

import { Modal } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CTA } from '@websrc/App/Components/CTA'
import { mapCoralVideoHero } from '@websrc/DataMappers/mapHero'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { OutlineIcon } from '../../../coral-library/icons/Outline.icon'
import { PauseIcon } from '../../../coral-library/icons/Pause.icon'
import { PlayIcon } from '../../../coral-library/icons/Play.icon'

type CoralVideoHeroProps = ReturnType<typeof mapCoralVideoHero>

export const CoralVideoHero: FC<CoralVideoHeroProps> = (props) => {
  const [scrollPadding, setScrollPadding] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  })
  const [containerWidth, setContainerWidth] = useState<string>('100%')
  const heroRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const getMaxPadding = useCallback(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 0
    if (width >= 1440) return { top: 80, right: 120, bottom: 80, left: 120 }
    if (width >= 1080) return { top: 80, right: 80, bottom: 80, left: 80 }
    if (width >= 720) return { top: 48, right: 48, bottom: 48, left: 48 }
    return { top: 24, right: 24, bottom: 24, left: 24 }
  }, [])

  useEffect(() => {
    const maxScroll = 150
    const minWidth = 1374
    const breakpoint = 1648
    const maxWidth = 1920

    function lerp(a: number, b: number, t: number) {
      return a + (b - a) * t
    }

    const handleScroll = () => {
      if (!heroRef.current) return
      const rect = heroRef.current.getBoundingClientRect()
      const scrollTop = Math.max(0, -rect.top)
      const viewportWidth = Math.min(window.innerWidth, maxWidth)

      if (viewportWidth > breakpoint) {
        const t = Math.min(scrollTop / maxScroll, 1)
        const widthPx = lerp(viewportWidth, minWidth, t)
        setContainerWidth(`${widthPx}px`)

        const max = getMaxPadding()
        setScrollPadding({
          top: Math.min(max.top, scrollTop),
          right: 0,
          bottom: Math.min(max.bottom, scrollTop),
          left: 0,
        })
      } else {
        setContainerWidth('100%')
        const max = getMaxPadding()
        setScrollPadding({
          top: Math.min(max.top, scrollTop),
          right: Math.min(max.right, scrollTop),
          bottom: Math.min(max.bottom, scrollTop),
          left: Math.min(max.left, scrollTop),
        })
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [getMaxPadding])

  const handleScrollToButton = (e: React.MouseEvent) => {
    e.preventDefault()
    const section = document.getElementById('coral-product-cards')
    if (section) section.scrollIntoView({ behavior: 'smooth' })
  }

  const handleOpenFullVideo = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsModalVisible(true)
  }

  return (
    <div className={'coral-video-hero'} ref={heroRef}>
      <Modal
        className={'coral-video-hero__modal-container'}
        closeCallback={() => setIsModalVisible(false)}
        customContent={true}
        loadOnInitial={{ enabled: true, visible: isModalVisible }}
        overlayBackgroundColor={'dark'}
        position={'center'}
      >
        <div className={'coral-video-hero__modal'}>
          <Asset
            altText={props.fullVideo?.altText}
            className={'how-it-works__main-asset'}
            forcePauseVideo={!isModalVisible}
            lazyLoading={true}
            objectFit={'cover'}
            srcSetWidths={[400, 600, 800, 1200, 1600]}
            thumbnail={props.fullVideo?.thumbnail}
            video={props.fullVideo?.video}
          />
        </div>
      </Modal>
      <div
        className={'coral-video-hero__scroll-container'}
        style={{
          width: containerWidth,
          padding: `${scrollPadding.top}px ${scrollPadding.right}px ${scrollPadding.bottom}px ${scrollPadding.left}px`,
          transition: 'width 0.2s ease-out, padding 0.2s ease-out',
        }}
      >
        <div className={'coral-video-hero__video'}>
          <Asset
            altText={'Placeholder Image'}
            animationClassName={'coral-video-hero__video-view'}
            forcePauseVideo={isPaused}
            loopAnimation={true}
            objectFit={'cover'}
            video={props.backgroundAnimation?.video}
          />
        </div>
        <div className={'coral-video-hero__content'}>
          <div className={'coral-video-hero__text-wrapper'}>
            <div className={'coral-video-hero__label'}>
              <div className={'coral-video-hero__button-fit-text'}>{props.label}</div>
            </div>
            <h1 className={'coral-video-hero__headline'}>
              <div className={'coral-video-hero__button-fit-text'}>{props.headline}</div>
            </h1>
            <div className={'coral-video-hero__description'}>
              <div className={'coral-video-hero__button-fit-text'}>{props.description}</div>
            </div>
            <div className={'coral-video-hero__buttons-container'}>
              <div className={'coral-video-hero__button-jumplink-container'}>
                <CTA
                  className={'coral-global-button m--type-fill m--theme-dark'}
                  cta={{
                    __ctaType: 'internal',
                    newTab: false,
                    text: props.buttonJumplink?.text ?? '',
                  }}
                  iconName={'arrow'}
                  onClick={handleScrollToButton}
                  type={'clean'}
                />
              </div>
              <div className={'coral-video-hero__button-video-popup-container'}>
                <CTA
                  className={'coral-global-button m--type-outline m--theme-dark'}
                  cta={{
                    __ctaType: 'internal',
                    newTab: false,
                    text: props.buttonModal?.text ?? '',
                  }}
                  iconName={'play'}
                  onClick={handleOpenFullVideo}
                  type={'clean'}
                />
              </div>
            </div>
          </div>
          <div className={'coral-video-hero__video-pause-container'}>
            <div
              className={'coral-video-hero__video-pause'}
              onClick={() => setIsPaused((prev) => !prev)}
            >
              <OutlineIcon />
              <div className={'coral-video-hero__video-pause-inner'}>
                {isPaused ? <PlayIcon /> : <PauseIcon />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
